import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getApiBase } from '../api/httpClient';
import './AuthCallback.css';

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Connecting to Google...');
  const [error, setError] = useState(false);

  useEffect(() => {
    // Prevent duplicate processing
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    console.log('[AuthCallback] Starting OAuth callback processing...');
    console.log('[AuthCallback] Location pathname:', location.pathname);
    console.log('[AuthCallback] Location search:', location.search);
    console.log('[AuthCallback] Location hash:', location.hash);
    console.log('[AuthCallback] Full URL:', window.location.href);

    // Set timeout for error state
    const timeout = setTimeout(() => {
      console.error('[AuthCallback] Timeout reached');
      setError(true);
      setStatus('Connection timeout. Please try again.');
    }, 15000); // Increased to 15 seconds for slow connections

    const processCallback = async () => {
      try {
        setStatus('Processing authentication...');
        
        // Extract session_id from various possible locations
        let sessionId = null;
        
        // 1. Try from react-router's location.search (HashRouter: everything after /#/auth/callback?)
        if (location.search && location.search.includes('session_id=')) {
          const searchParams = new URLSearchParams(location.search);
          sessionId = searchParams.get('session_id');
          console.log('[AuthCallback] Found session_id in location.search');
        }
        
        // 2. Try from window.location.search (before hash, unlikely with HashRouter but check)
        if (!sessionId && window.location.search && window.location.search.includes('session_id=')) {
          const searchParams = new URLSearchParams(window.location.search);
          sessionId = searchParams.get('session_id');
          console.log('[AuthCallback] Found session_id in window.location.search');
        }
        
        // 3. Try from hash - various formats
        if (!sessionId) {
          const fullHash = window.location.hash;
          console.log('[AuthCallback] Checking hash:', fullHash);
          
          if (fullHash && fullHash.includes('session_id=')) {
            // Handle: /#/auth/callback?session_id=xxx OR /#session_id=xxx
            const match = fullHash.match(/session_id=([^&]+)/);
            if (match) {
              sessionId = match[1];
              console.log('[AuthCallback] Found session_id via regex in hash');
            }
          }
        }

        if (!sessionId) {
          console.error('[AuthCallback] No session_id found in URL');
          console.error('[AuthCallback] location.search:', location.search);
          console.error('[AuthCallback] window.location.search:', window.location.search);
          console.error('[AuthCallback] window.location.hash:', window.location.hash);
          setError(true);
          setStatus('Authentication failed. No session ID received. Please try again.');
          clearTimeout(timeout);
          return;
        }

        console.log('[AuthCallback] Processing session_id:', sessionId.substring(0, 10) + '...');
        setStatus('Fetching your profile...');
        
        // Exchange session_id for user data and token.
        // iter299 — Use `getApiBase()` (same-origin on production custom
        // domains, env URL on preview/dev) so OAuth callbacks survive
        // custom-domain deploys without rebuilding the frontend.
        const response = await fetch(
          `${getApiBase()}/api/auth/oauth/callback`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ session_id: sessionId })
          }
        );

        console.log('[AuthCallback] OAuth callback response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          
          console.log('[AuthCallback] OAuth success:', { 
            user_id: data.user?.user_id, 
            email: data.user?.email,
            is_new_user: data.is_new_user,
            needs_onboarding: data.needs_onboarding,
            role: data.user?.role,
            has_token: !!data.token
          });
          
          if (!data.token || !data.user) {
            console.error('[AuthCallback] Missing token or user in response');
            setError(true);
            setStatus('Authentication failed. Please try again.');
            clearTimeout(timeout);
            return;
          }
          
          setStatus('Success! Redirecting...');
          
          // IMPORTANT: Set auth state FIRST, then navigate
          // This ensures the token is stored before any route guards check
          // Pass needs_onboarding to trigger role selection modal
          try {
            login(data.token, data.user, data.is_new_user, data.needs_onboarding);
            console.log('[AuthCallback] Login called successfully, needs_onboarding:', data.needs_onboarding);
          } catch (loginError) {
            console.error('[AuthCallback] Login function error:', loginError);
            toast.error('Login failed. Please try again.');
            setError(true);
            setStatus('Login failed. Please try again.');
            clearTimeout(timeout);
            return;
          }
          
          clearTimeout(timeout);
          
          // Wait a moment to ensure auth state is propagated
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Navigate based on onboarding status — backend is source of truth
          const onboardingDone = data.user?.onboarding_completed || data.user?.onboardingCompleted;
          const hasAccountType = !!(data.user?.account_type || data.user?.accountType || data.user?.profile_type);
          
          if (!onboardingDone && !hasAccountType) {
            console.log('[AuthCallback] New user needs onboarding, navigating to /onboarding');
            navigate('/onboarding', { replace: true });
          } else {
            console.log('[AuthCallback] Navigating to home...');
            navigate('/', { replace: true });
          }
          
        } else {
          let errorDetail = 'Unknown error';
          try {
            const errorData = await response.json();
            errorDetail = errorData.detail || errorDetail;
          } catch (parseError) {
            console.error('[AuthCallback] Failed to parse error response');
          }
          
          console.error('[AuthCallback] OAuth callback failed:', {
            status: response.status,
            error: errorDetail
          });
          
          // Parse error code from detail
          let errorMessage = errorDetail;
          
          if (errorDetail.includes('INVALID_SESSION')) {
            errorMessage = 'Your Google session expired. Please try again.';
          } else if (errorDetail.includes('OAUTH_FAILED')) {
            errorMessage = 'Google authentication failed. Please try again.';
          } else if (errorDetail.includes('SESSION_CREATE_FAILED')) {
            errorMessage = 'Failed to create your session. Please try again.';
          } else if (response.status === 401) {
            errorMessage = 'Session expired. Please sign in again.';
          } else if (response.status >= 500) {
            errorMessage = 'Server error. Please try again later.';
          }
          
          setError(true);
          setStatus(errorMessage);
          toast.error(errorMessage);
          clearTimeout(timeout);
        }
      } catch (error) {
        console.error('[AuthCallback] OAuth error:', error);
        setError(true);
        const errorMessage = 'Network error during sign in. Please check your connection.';
        setStatus(errorMessage);
        toast.error(errorMessage);
        clearTimeout(timeout);
      }
    };

    processCallback();

    return () => {
      clearTimeout(timeout);
    };
  }, [location, navigate, login, t]);

  const handleRetry = () => {
    // Clear the hash and navigate to welcome
    window.location.hash = '';
    navigate('/welcome', { replace: true });
  };

  return (
    <div className="auth-callback-page" data-testid="auth-callback">
      <div className="callback-container">
        {!error ? (
          <>
            <div className="spinner"></div>
            <p className="callback-status">{status}</p>
          </>
        ) : (
          <>
            <div className="error-icon">❌</div>
            <p className="callback-error">{status}</p>
            <button
              onClick={handleRetry}
              className="btn btn-primary"
              data-testid="retry-auth"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;
